<template>
  <!-- Compact layout: simple flex row for certificates/pathways -->
  <div
    v-if="layout === 'compact'"
    class="d-flex flex-column flex-md-row mt-3 mt-md-0 ms-0 ms-md-4 w-100 w-md-auto"
    style="gap: 12px"
  >
    <!-- View details button -->
    <UiButton v-if="showDetailsButton" variant="dark-outline" @click="$emit('view-details')">
      View details
      <template #endIcon>
        <v-icon size="16" class="ms-1">mdi-open-in-new</v-icon>
      </template>
    </UiButton>

    <!-- Selected state (with checkmark) -->
    <UiButton v-if="isSelected" variant="maroon" @click="$emit('remove-course')">
      <template #endIcon>
        <v-icon color="white" size="20">mdi-check</v-icon>
      </template>
      Added to schedule
    </UiButton>

    <!-- Disabled with tooltip -->
    <InfoTooltip v-else-if="isButtonDisabled && course.ui?.disabledTooltip">
      <template #activator="{ props }">
        <UiButton v-bind="props" :variant="course.ui?.buttonVariant || 'dark-outline'" :disabled="true">
          {{ course.ui?.buttonLabel || 'Add to schedule' }}
        </UiButton>
      </template>
      {{ course.ui.disabledTooltip }}
    </InfoTooltip>

    <!-- Disabled without tooltip -->
    <UiButton v-else-if="isButtonDisabled" :variant="course.ui?.buttonVariant || 'dark-outline'" :disabled="true">
      {{ buttonText }}
    </UiButton>

    <!-- Link button -->
    <UiButton
      v-else-if="course.section?.overrides?.ui?.isLink && course.section?.overrides?.ui?.linkUrl"
      :variant="course.ui?.buttonVariant || 'dark-outline'"
      @click="$emit('open-link')"
    >
      {{ buttonText }}
    </UiButton>

    <!-- Dropdown -->
    <ScheduleDropdown
      v-else-if="course.ui?.showDropdown !== false"
      v-model="scheduleDropdownOpenLocal"
      :offerings="course.offerings"
      trigger="click"
      :button-variant="course.ui?.buttonVariant || 'maroon'"
      :button-text="buttonText || 'Add to schedule'"
      @select="$emit('offering-selection', $event)"
    />

    <!-- Simple action button -->
    <UiButton v-else :variant="course.ui?.buttonVariant || 'maroon'" @click="$emit('button-action')">
      {{ buttonText }}
    </UiButton>
  </div>

  <!-- Full layout: grid-based for CourseCard -->
  <v-col
    v-else
    :cols="isSmallScreen && !$vuetify.display.xs ? '4' : '12'"
    sm="6"
    :md="isSmallScreen ? '4' : '6'"
    lg="6"
    xl="6"
    class="align-self-end"
  >
    <v-row
      no-gutters
      :class="[
        isSmallScreen && !$vuetify.display.xs ? 'flex-row justify-center' : 'flex-column flex-md-row justify-end'
      ]"
      :style="$vuetify.display.xs || isSmallScreen ? 'gap: 8px;' : 'gap: 12px;'"
    >
      <!-- View details button -->
      <v-col cols="12" sm="auto" class="pa-0 d-flex justify-center" v-if="showDetailsButton">
        <UiButton
          variant="dark-outline"
          :block="$vuetify.display.xs"
          :style="buttonSizeStyles"
          @click="$emit('view-details')"
        >
          {{ displayInfo ? 'Close details' : 'View details' }}
        </UiButton>
      </v-col>

      <!-- Primary action button -->
      <v-col cols="12" sm="auto" class="pa-0 d-flex justify-center">
        <!-- Selected state (with checkmark) - Local UI state -->
        <UiButton
          v-if="isSelected"
          variant="maroon"
          :block="$vuetify.display.xs"
          :style="buttonSizeStyles"
          @click="$emit('remove-course')"
        >
          <template #endIcon>
            <v-icon color="white" size="20">mdi-check</v-icon>
          </template>
          Added to schedule
        </UiButton>

        <!-- Disabled states (Transcripted, Transfer Credit, Prereq Required, Enrolled/Scheduled) -->
        <InfoTooltip v-else-if="isButtonDisabled && course.ui?.disabledTooltip">
          <template #activator="{ props }">
            <UiButton
              v-bind="props"
              :variant="course.ui?.buttonVariant || 'dark-outline'"
              :disabled="true"
              :block="$vuetify.display.xs"
              :style="buttonSizeStyles"
            >
              {{ course.ui?.buttonLabel || 'Add to schedule' }}
            </UiButton>
          </template>
          {{ course.ui.disabledTooltip }}
        </InfoTooltip>

        <!-- Disabled states without tooltip -->
        <UiButton
          v-else-if="isButtonDisabled"
          :variant="course.ui?.buttonVariant || 'dark-outline'"
          :disabled="true"
          :block="$vuetify.display.xs"
          :style="buttonSizeStyles"
        >
          {{ buttonText }}
        </UiButton>

        <!-- Link button (when backend specifies isLink and linkUrl) -->
        <UiButton
          v-else-if="course.section?.overrides?.ui?.isLink && course.section?.overrides?.ui?.linkUrl"
          :variant="course.ui?.buttonVariant || 'dark-outline'"
          :block="$vuetify.display.xs"
          :style="buttonSizeStyles"
          @click="$emit('open-link')"
        >
          {{ buttonText }}
        </UiButton>

        <!-- Add to schedule dropdown - Multiple offerings available -->
        <ScheduleDropdown
          v-else-if="course.ui?.showDropdown !== false"
          v-model="scheduleDropdownOpenLocal"
          :offerings="course.offerings"
          trigger="click"
          :button-variant="course.ui?.buttonVariant || 'maroon'"
          :button-text="buttonText || 'Add to schedule'"
          :button-block="$vuetify.display.xs"
          :button-style="buttonSizeStyles"
          @select="$emit('offering-selection', $event)"
        />

        <!-- Simple action button - For actions like "Add to Transcript" -->
        <UiButton
          v-else
          :variant="course.ui?.buttonVariant || 'maroon'"
          :block="$vuetify.display.xs"
          :style="buttonSizeStyles"
          @click="$emit('button-action')"
        >
          {{ buttonText }}
        </UiButton>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { defineComponent } from 'vue';
import { Button as UiButton } from '../ui/index.js';
import InfoTooltip from '../ui/InfoTooltip.vue';
import ScheduleDropdown from './ScheduleDropdown.vue';

export default defineComponent({
  name: 'CourseCardButtons',
  components: {
    UiButton,
    InfoTooltip,
    ScheduleDropdown
  },
  props: {
    course: {
      type: Object,
      required: true
    },
    /**
     * Layout mode: 'full' uses v-col/v-row grid (for CourseCard),
     * 'compact' uses simple flex row (for CourseCardCompact).
     */
    layout: {
      type: String,
      default: 'full',
      validator: (value) => ['full', 'compact'].includes(value)
    },
    showDetailsButton: {
      type: Boolean,
      default: false
    },
    displayInfo: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isButtonDisabled: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String,
      default: ''
    },
    scheduleDropdownOpen: {
      type: Boolean,
      default: false
    },
    isSmallScreen: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'view-details',
    'remove-course',
    'open-link',
    'offering-selection',
    'button-action',
    'update:scheduleDropdownOpen'
  ],
  computed: {
    scheduleDropdownOpenLocal: {
      get() {
        return this.scheduleDropdownOpen;
      },
      set(value) {
        this.$emit('update:scheduleDropdownOpen', value);
      }
    },
    buttonSizeStyles() {
      if (this.isSmallScreen && !this.$vuetify.display.xs) {
        return 'min-width: 180px;';
      } else if (this.$vuetify.display.smAndUp && !this.isSmallScreen) {
        return 'min-width: 180px;';
      }
      return '';
    }
  }
});
</script>
